import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Table, Button, Input, Space, Card, Upload, message, Image, Row, Col,
  Typography, Tag, Modal, Progress, Tooltip, ConfigProvider, theme as antTheme
} from "antd";
import {
  DeleteOutlined, PlusOutlined, UploadOutlined, EditOutlined, SaveOutlined,
  DownloadOutlined, DatabaseOutlined, ShoppingOutlined, FileExcelOutlined,
  PictureOutlined, AppstoreOutlined, CloudUploadOutlined, CloseOutlined,
  TagsOutlined
} from "@ant-design/icons";
import config from "../config";

const { Text } = Typography;
const { Search } = Input;

const UpdateProductIds = ({ darkTheme = false }) => {
  const [productUpdates, setProductUpdates] = useState([]);
  const [previousProducts, setPreviousProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [editingId, setEditingId] = useState(null); // _id of the row being edited
  const [searchValue, setSearchValue] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredProducts.length, searchValue]);

  useEffect(() => {
    fetchPreviousProducts();
  }, []);

  const fetchPreviousProducts = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/get-previous-products`);
      setPreviousProducts(response.data);
      setFilteredProducts(response.data);
      setSearchValue("");
    } catch (error) {
      messageApi.error("Failed to fetch products");
      console.error("Error fetching previous products:", error);
    }
  };

  const generateSampleExcel = () => {
    const sampleData = [
      { productID: "PROD001", productName: "Sample Product 1", updatedID: "NEW001", sku: "SKU001", productLocation: "Warehouse A", productCategory: "Electronics", imageUrl: "https://via.placeholder.com/150" },
      { productID: "PROD002", productName: "Sample Product 2", updatedID: "NEW002", sku: "SKU002", productLocation: "Warehouse B", productCategory: "Clothing", imageUrl: "https://via.placeholder.com/150" }
    ];
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Products");
    XLSX.writeFile(workbook, "sample_product_template.xlsx");
    messageApi.success("Sample template downloaded");
  };

  const downloadAllProductsExcel = () => {
    if (previousProducts.length === 0) {
      messageApi.warning("No products available to export");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(previousProducts);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Products");
    XLSX.writeFile(workbook, `all_products_${new Date().getTime()}.xlsx`);
    messageApi.success("All products exported");
  };

  const handleFileUpload = (file) => {
    setIsUploading(true);
    setUploadProgress(0);
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => (prev >= 90 ? 90 : prev + 10));
    }, 100);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const formattedData = jsonData.map((row, index) => ({
          key: index,
          productID: row["productID"]?.toString() || "",
          productName: row["productName"] || "",
          updatedID: row["updatedID"]?.toString() || "",
          sku: row["sku"] || "",
          productLocation: row["productLocation"] || "",
          productCategory: row["productCategory"] || "",
          imageUrl: row["imageUrl"] || ""
        }));

        clearInterval(progressInterval);
        setUploadProgress(100);
        setTimeout(() => {
          setProductUpdates(formattedData);
          setIsUploading(false);
          setUploadProgress(0);
          messageApi.success(`Loaded ${formattedData.length} products from file`);
        }, 400);
      } catch (error) {
        clearInterval(progressInterval);
        setIsUploading(false);
        setUploadProgress(0);
        messageApi.error("Could not read the Excel file. Check the format against the sample template.");
        console.error("File read error:", error);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  // New rows (pending updates) are edited by index; existing products are edited by _id
  const handleUpdateChange = (index, field, value) => {
    const data = [...productUpdates];
    data[index] = { ...data[index], [field]: value };
    setProductUpdates(data);
  };

  const handleExistingChange = (id, field, value) => {
    setFilteredProducts((rows) => rows.map((r) => (r._id === id ? { ...r, [field]: value } : r)));
  };

  const handleAddRow = () => {
    setProductUpdates([
      ...productUpdates,
      { key: Date.now(), productID: "", updatedID: "", productName: "", sku: "", productLocation: "", productCategory: "", imageUrl: "" }
    ]);
  };

  const handleRemoveRow = (index) => {
    const updates = [...productUpdates];
    updates.splice(index, 1);
    setProductUpdates(updates);
  };

  const handleSubmit = async () => {
    if (productUpdates.length === 0) {
      messageApi.warning("Add products first");
      return;
    }
    const hasEmptyFields = productUpdates.some(
      (u) => !u.productID || !u.productName || !u.updatedID || !u.sku || !u.productLocation || !u.productCategory
    );
    if (hasEmptyFields) {
      messageApi.error("Fill in every required field before submitting");
      return;
    }
    try {
      const response = await axios.post(`${config.apiBaseUrl}/api/update-product-ids`, { productUpdates });
      messageApi.success(response.data.message || "Products updated");
      fetchPreviousProducts();
      setProductUpdates([]);
    } catch (error) {
      messageApi.error("Failed to update product IDs");
      console.error("Update Error:", error);
    }
  };

  const handleSaveEditedRow = async () => {
    try {
      const editedProduct = filteredProducts.find((p) => p._id === editingId);
      if (!editedProduct) return;
      await axios.post(`${config.apiBaseUrl}/api/update-product-ids`, { productUpdates: [editedProduct] });
      messageApi.success("Product updated");
      setEditingId(null);
      fetchPreviousProducts();
    } catch (error) {
      messageApi.error("Failed to save product");
      console.error("Edit Save Error:", error);
    }
  };

  const applySearch = (value) => {
    const term = (value || "").trim().toLowerCase();
    if (!term) {
      setFilteredProducts(previousProducts);
      return;
    }
    setFilteredProducts(
      previousProducts.filter((p) =>
        [p.productName, p.productID, p.updatedID, p.sku, p.productLocation, p.productCategory, p.imageUrl]
          .some((f) => String(f ?? "").toLowerCase().includes(term))
      )
    );
  };

  const handleSearchChange = (value) => {
    setSearchValue(value || "");
    setEditingId(null);
    applySearch(value);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    applySearch(searchValue); // restores unsaved edits from the original data
  };

  const handleDeleteProduct = (product) => {
    Modal.confirm({
      title: "Delete product",
      content: `Delete "${product.productName || product.productID}"? This can't be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.delete(`${config.apiBaseUrl}/api/delete-product/${product._id}`);
          messageApi.success("Product deleted");
          fetchPreviousProducts();
        } catch (error) {
          messageApi.error("Failed to delete product");
          console.error("Delete Error:", error);
        }
      }
    });
  };

  /* ── Columns ── */

  const updateInput = (field, placeholder) => (text, _record, index) => (
    <Input value={text} onChange={(e) => handleUpdateChange(index, field, e.target.value)} placeholder={placeholder} size="small" />
  );

  const updateColumns = [
    { title: "#", key: "index", width: 56, align: "center", render: (_, __, i) => <Text type="secondary">{i + 1}</Text> },
    { title: "Product ID", dataIndex: "productID", width: 140, render: updateInput("productID", "Product ID") },
    { title: "Product name", dataIndex: "productName", width: 200, render: updateInput("productName", "Product name") },
    { title: "Updated ID", dataIndex: "updatedID", width: 140, render: updateInput("updatedID", "Updated ID") },
    { title: "SKU", dataIndex: "sku", width: 120, render: updateInput("sku", "SKU") },
    { title: "Location", dataIndex: "productLocation", width: 140, render: updateInput("productLocation", "Location") },
    { title: "Category", dataIndex: "productCategory", width: 140, render: updateInput("productCategory", "Category") },
    { title: "Image URL", dataIndex: "imageUrl", width: 200, render: updateInput("imageUrl", "Image URL") },
    {
      title: "", key: "action", width: 56, align: "center",
      render: (_, __, index) => (
        <Tooltip title="Remove row">
          <Button danger type="text" size="small" icon={<DeleteOutlined />} aria-label="Remove row" onClick={() => handleRemoveRow(index)} />
        </Tooltip>
      )
    }
  ];

  const existingCell = (field, view) => (text, record) =>
    editingId === record._id ? (
      <Input value={text} onChange={(e) => handleExistingChange(record._id, field, e.target.value)} size="small" />
    ) : (
      view(text)
    );

  const previousColumns = [
    {
      title: "#", key: "index", width: 56, align: "center",
      render: (_, __, i) => <Text type="secondary">{(currentPage - 1) * pageSize + i + 1}</Text>
    },
    {
      title: "Image", dataIndex: "imageUrl", width: 72,
      render: (text) =>
        text ? (
          <button type="button" className="pm-thumb" aria-label="Preview image" onClick={() => { setPreviewImage(text); setPreviewVisible(true); }}>
            <Image src={text} alt="Product" width={40} height={40} preview={false} style={{ objectFit: "cover" }} fallback="https://via.placeholder.com/40?text=No+Image" />
          </button>
        ) : (
          <div className="pm-thumb empty"><PictureOutlined /></div>
        )
    },
    {
      title: "Product name", dataIndex: "productName", width: 220,
      render: existingCell("productName", (t) => <Text strong>{t}</Text>)
    },
    {
      title: "Product ID", dataIndex: "productID", width: 120,
      render: existingCell("productID", (t) => <Tag className="pm-tag id">{t}</Tag>)
    },
    {
      title: "Updated ID", dataIndex: "updatedID", width: 120,
      render: existingCell("updatedID", (t) => <Tag className="pm-tag ok">{t}</Tag>)
    },
    {
      title: "SKU", dataIndex: "sku", width: 120,
      render: existingCell("sku", (t) => <span className="pm-mono">{t}</span>)
    },
    {
      title: "Location", dataIndex: "productLocation", width: 130,
      render: existingCell("productLocation", (t) => <Tag className="pm-tag warn">{t}</Tag>)
    },
    {
      title: "Category", dataIndex: "productCategory", width: 130,
      render: existingCell("productCategory", (t) => <Tag className="pm-tag neutral">{t}</Tag>)
    },
    {
      title: "Actions", key: "action", width: 170, fixed: "right",
      render: (_, record) =>
        editingId === record._id ? (
          <Space size={4}>
            <Button type="primary" size="small" icon={<SaveOutlined />} onClick={handleSaveEditedRow}>Save</Button>
            <Button size="small" icon={<CloseOutlined />} onClick={handleCancelEdit}>Cancel</Button>
          </Space>
        ) : (
          <Space size={0}>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditingId(record._id)}>Edit</Button>
            <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteProduct(record)}>Delete</Button>
          </Space>
        )
    }
  ];

  const withImages = previousProducts.filter((p) => p.imageUrl).length;
  const categories = new Set(previousProducts.map((p) => p.productCategory).filter(Boolean)).size;

  return (
    <ConfigProvider
      theme={{
        algorithm: darkTheme ? antTheme?.darkAlgorithm : antTheme?.defaultAlgorithm,
        token: {
          colorPrimary: darkTheme ? "#2dd4bf" : "#0f766e",
          borderRadius: 8,
          fontFamily: "'Figtree', system-ui, -apple-system, 'Segoe UI', sans-serif"
        }
      }}
    >
      <div className={`product-master-container ${darkTheme ? "dark-mode" : "light-mode"}`}>
        <style>
          {`
          @import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');

          .product-master-container {
            --bg-page:#eef3f2; --bg-card:#ffffff; --bg-hover:#f0f5f4;
            --border:#d3ddda; --border-light:#e5ebe9;
            --text-primary:#10231f; --text-secondary:#435852; --text-muted:#6b7f78;
            --brand:#0f766e; --brand-dark:#0a5c56; --brand-soft:#d9f0ec;
            --ok-bg:#d8f3e4; --ok-fg:#0b5d34; --warn-bg:#fdebc8; --warn-fg:#7a4204;
            --info-bg:#dbe7fd; --info-fg:#1e40af; --bad-bg:#fbdcdc; --bad-fg:#8f1d1d;
            --shadow-sm:0 1px 2px rgba(16,35,31,.06);
            font-family:'Figtree',system-ui,-apple-system,'Segoe UI',sans-serif;
            font-size:14px; min-height:100vh; background:var(--bg-page); color:var(--text-primary);
          }
          .product-master-container.dark-mode {
            --bg-page:#0c1614; --bg-card:#14211e; --bg-hover:#1b2d29;
            --border:#2a3f39; --border-light:#22342f;
            --text-primary:#e6f0ed; --text-secondary:#a3b8b1; --text-muted:#7b918a;
            --brand:#2dd4bf; --brand-dark:#14b8a6; --brand-soft:#16403a;
            --ok-bg:#0f3d2a; --ok-fg:#86efac; --warn-bg:#4a3208; --warn-fg:#fcd34d;
            --info-bg:#172f57; --info-fg:#93c5fd; --bad-bg:#4a1717; --bad-fg:#fca5a5;
          }
          .product-master-container * { box-sizing:border-box; }
          .product-master-container .anticon { line-height:0; }

          /* header */
          .pm-header { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 24px; background:var(--bg-card); border-bottom:1px solid var(--border); position:sticky; top:0; z-index:100; }
          .pm-header-left { display:flex; align-items:center; gap:12px; }
          .pm-header-icon { display:grid; place-items:center; width:38px; height:38px; border-radius:10px; background:var(--brand); color:#fff; font-size:18px; }
          .dark-mode .pm-header-icon { color:#052e2a; }
          .pm-title { margin:0; font-size:18px; font-weight:700; letter-spacing:-.01em; line-height:1.2; color:var(--text-primary); }
          .pm-sub { margin:0; font-size:13px; color:var(--text-secondary); }
          .pm-pending { display:inline-flex; align-items:center; gap:8px; padding:5px 12px; border-radius:999px; font-size:13px; font-weight:600; background:var(--bg-hover); color:var(--text-secondary); border:1px solid var(--border-light); }
          .pm-pending.on { background:var(--warn-bg); color:var(--warn-fg); border-color:transparent; }

          /* page column */
          .pm-page {  margin:0 auto; padding:16px 24px 32px; display:flex; flex-direction:column; gap:14px; }

          /* stat strip */
          .pm-stats { display:grid; grid-template-columns:repeat(4,1fr); background:var(--bg-card); border:1px solid var(--border); border-radius:12px; overflow:hidden; box-shadow:var(--shadow-sm); }
          .pm-stat { display:grid; grid-template-columns:auto 1fr; grid-template-areas:"icon num" "icon lbl"; column-gap:12px; align-items:center; padding:14px 18px; border-left:1px solid var(--border-light); }
          .pm-stat:first-child { border-left:0; }
          .pm-stat-icon { grid-area:icon; width:38px; height:38px; border-radius:10px; display:grid; place-items:center; font-size:17px; }
          .pm-stat-icon.green { background:var(--brand-soft); color:var(--brand); }
          .pm-stat-icon.amber { background:var(--warn-bg); color:var(--warn-fg); }
          .pm-stat-icon.blue { background:var(--info-bg); color:var(--info-fg); }
          .pm-stat-icon.teal { background:var(--ok-bg); color:var(--ok-fg); }
          .pm-stat-num { grid-area:num; font-size:24px; font-weight:700; line-height:1.1; font-variant-numeric:tabular-nums; }
          .pm-stat-lbl { grid-area:lbl; font-size:12px; color:var(--text-secondary); }

          /* cards */
          .product-master-container .ant-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; box-shadow:var(--shadow-sm); }
          .product-master-container .ant-card-head { border-bottom:1px solid var(--border-light); min-height:52px; }
          .product-master-container .ant-card-head-title { font-weight:700; }
          .pm-card-title { display:flex; align-items:center; gap:8px; }
          .pm-card-title .anticon { color:var(--brand); font-size:16px; }
          .pm-count { font-size:12px; font-weight:600; color:var(--brand); background:var(--brand-soft); padding:2px 10px; border-radius:999px; }

          /* upload */
          .pm-upload { border:1.5px dashed var(--border); border-radius:12px; padding:24px 16px; text-align:center; background:var(--bg-hover); transition:border-color .15s, background .15s; }
          .pm-upload:hover { border-color:var(--brand); background:var(--brand-soft); }
          .pm-upload-icon { display:grid; place-items:center; width:48px; height:48px; margin:0 auto 10px; border-radius:12px; background:var(--brand-soft); color:var(--brand); font-size:22px; }
          .pm-upload h4 { margin:0 0 2px; font-size:15px; font-weight:700; color:var(--text-primary); }
          .pm-upload p { margin:0 0 12px; font-size:13px; color:var(--text-secondary); }
          .pm-quick { display:flex; justify-content:center; gap:4px; margin:12px 0 16px; flex-wrap:wrap; }

          .pm-empty { text-align:center; padding:32px 16px; color:var(--text-secondary); }
          .pm-empty h4 { margin:8px 0 2px; font-size:15px; font-weight:700; color:var(--text-primary); }
          .pm-empty .anticon { font-size:30px; color:var(--text-muted); }
          .pm-submit { display:flex; justify-content:flex-end; padding-top:14px; }

          /* table */
          .product-master-container .ant-table { background:transparent; }
          .product-master-container .ant-table-thead > tr > th { background:var(--bg-hover); font-size:12px; font-weight:700; color:var(--text-secondary); }
          .product-master-container .ant-table-tbody > tr > td { border-bottom-color:var(--border-light); }
          .pm-tag { margin:0; border:0; font-weight:600; border-radius:999px; padding:0 10px; }
          .pm-tag.id { background:var(--info-bg); color:var(--info-fg); }
          .pm-tag.ok { background:var(--ok-bg); color:var(--ok-fg); }
          .pm-tag.warn { background:var(--warn-bg); color:var(--warn-fg); }
          .pm-tag.neutral { background:var(--brand-soft); color:var(--brand); }
          .pm-mono { font-family:'IBM Plex Mono',ui-monospace,monospace; font-size:12px; background:var(--bg-hover); padding:2px 6px; border-radius:6px; }
          .pm-thumb { display:grid; place-items:center; width:40px; height:40px; padding:0; border:1px solid var(--border); border-radius:8px; overflow:hidden; background:var(--bg-hover); cursor:pointer; }
          .pm-thumb.empty { cursor:default; color:var(--text-muted); }
          .pm-thumb:focus-visible { outline:none; box-shadow:0 0 0 3px rgba(15,118,110,.28); }

          @media (max-width:1200px) { .pm-stats { grid-template-columns:repeat(2,1fr); } .pm-stat:nth-child(3) { border-left:0; } .pm-stat:nth-child(n+3) { border-top:1px solid var(--border-light); } }
          @media (max-width:768px) {
            .pm-header { padding:10px 14px; flex-wrap:wrap; }
            .pm-page { padding:12px 14px 24px; }
            .pm-stat { padding:12px 14px; }
          }
          @media (prefers-reduced-motion:reduce) { .product-master-container * { transition:none !important; } }
        `}
        </style>

        {contextHolder}

        <header className="pm-header">
          <div className="pm-header-left">
            <span className="pm-header-icon"><AppstoreOutlined /></span>
            <div>
              <h1 className="pm-title">Product master</h1>
              <p className="pm-sub">Update product IDs, locations and categories in bulk</p>
            </div>
          </div>
          <span className={`pm-pending ${productUpdates.length > 0 ? "on" : ""}`}>
            <CloudUploadOutlined /> {productUpdates.length} pending {productUpdates.length === 1 ? "update" : "updates"}
          </span>
        </header>

        <main className="pm-page">
          <section className="pm-stats" aria-label="Summary">
            <div className="pm-stat"><div className="pm-stat-icon green"><DatabaseOutlined /></div><div className="pm-stat-num">{previousProducts.length}</div><div className="pm-stat-lbl">Total products</div></div>
            <div className="pm-stat"><div className="pm-stat-icon amber"><ShoppingOutlined /></div><div className="pm-stat-num">{productUpdates.length}</div><div className="pm-stat-lbl">Pending updates</div></div>
            <div className="pm-stat"><div className="pm-stat-icon blue"><PictureOutlined /></div><div className="pm-stat-num">{withImages}</div><div className="pm-stat-lbl">With images</div></div>
            <div className="pm-stat"><div className="pm-stat-icon teal"><TagsOutlined /></div><div className="pm-stat-num">{categories}</div><div className="pm-stat-lbl">Categories</div></div>
          </section>

          <Row gutter={[16, 16]}>
            {/* Updates */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span className="pm-card-title">
                    <FileExcelOutlined /> Product updates
                    {productUpdates.length > 0 && <span className="pm-count">{productUpdates.length} items</span>}
                  </span>
                }
                extra={
                  <Space>
                    <Button icon={<PlusOutlined />} onClick={handleAddRow}>Add row</Button>
                    {productUpdates.length > 0 && (
                      <Button danger onClick={() => setProductUpdates([])}>Clear all</Button>
                    )}
                  </Space>
                }
              >
                <div className="pm-upload">
                  <Upload beforeUpload={handleFileUpload} accept=".xlsx, .xls" showUploadList={false} disabled={isUploading}>
                    <div>
                      <div className="pm-upload-icon"><FileExcelOutlined /></div>
                      <h4>{isUploading ? "Reading file…" : "Upload an Excel file"}</h4>
                      <p>Drop a .xlsx or .xls file here, or choose one to browse</p>
                      <Button type="primary" icon={<UploadOutlined />} loading={isUploading}>
                        {isUploading ? "Reading…" : "Choose file"}
                      </Button>
                    </div>
                  </Upload>
                  {isUploading && <Progress percent={uploadProgress} status="active" style={{ marginTop: 12 }} />}
                </div>

                <div className="pm-quick">
                  <Tooltip title="Excel file with the required columns">
                    <Button type="text" icon={<DownloadOutlined />} onClick={generateSampleExcel}>Sample template</Button>
                  </Tooltip>
                  <Tooltip title="Export every product to Excel">
                    <Button type="text" icon={<DownloadOutlined />} onClick={downloadAllProductsExcel}>Export all products</Button>
                  </Tooltip>
                </div>

                {productUpdates.length > 0 ? (
                  <>
                    <Table columns={updateColumns} dataSource={productUpdates} pagination={false} size="small" scroll={{ x: 900, y: 420 }} />
                    <div className="pm-submit">
                      <Button type="primary" size="large" icon={<CloudUploadOutlined />} onClick={handleSubmit}>
                        Update {productUpdates.length} products
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="pm-empty">
                    <FileExcelOutlined />
                    <h4>No pending updates</h4>
                    <div>Upload an Excel file or add a row to start.</div>
                  </div>
                )}
              </Card>
            </Col>

            {/* Existing */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span className="pm-card-title">
                    <DatabaseOutlined /> Existing products
                    <span className="pm-count">{filteredProducts.length} items</span>
                  </span>
                }
                extra={
                  <Search
                    placeholder="Search name, ID, SKU, location…"
                    value={searchValue}
                    onChange={(e) => handleSearchChange(e?.target?.value ?? "")}
                    onSearch={(value) => handleSearchChange(value)}
                    style={{ width: 260 }}
                    allowClear
                  />
                }
              >
                <Table
                  columns={previousColumns}
                  dataSource={filteredProducts}
                  rowKey={(record, index) => record._id || record.productID || index}
                  pagination={{
                    current: currentPage,
                    pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "30"],
                    onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                  }}
                  size="small"
                  scroll={{ x: 900, y: 460 }}
                />
              </Card>
            </Col>
          </Row>
        </main>

        <Modal open={previewVisible} footer={null} onCancel={() => setPreviewVisible(false)} width={420} centered title="Product image">
          <div style={{ textAlign: "center" }}>
            <Image src={previewImage} alt="Product preview" style={{ maxWidth: "100%", maxHeight: 400, borderRadius: 8 }} preview={false} />
          </div>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default UpdateProductIds;